import React, { useState } from "react";
import { Button, TextField } from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import CloseIcon from "@mui/icons-material/Close";
import DatePickerWithTimeComponent from "../DatePickerWithTimeComponent";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { showCustomToast } from "../Misc/CustomToast";
import UploaderDocForNote from "../Updloader/UploaderDocForNote";
import UploadedFilesComponent from "../Updloader/UploadedFilesComponent";
import "./EditMedicalNoteRedesign.css";

function EditMedicalNote(props) {
  const { currentUser } = useAuth();

  const [quickNoteText, setQuickNoteText] = useState(props.note);
  const [effectiveDateTime, setEffectiveDateTime] = useState(
    props.effectiveDate
  );
  const [isLoading, setIsLoading] = useState(false);
  const [fileLinks, setFileLinks] = useState(props.fileLinks || []);
  const [files, setFiles] = useState([]);
  const [hasChanged, setHasChanged] = useState(false);

  const handleDateTimeChange = (event) => {
    setEffectiveDateTime(event);
    setHasChanged(true);
  };

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files.length <= 3) {
      setFiles(event.target.files);
      setHasChanged(true);
    } else {
      showCustomToast("You can only upload up to 3 files.", "error");
    }
  };

  const deleteFile = async (fileUrl, fileName, noteId) => {
    const storage = getStorage();
    const filePath = `Documents/medicalNotes/${noteId}/${fileName}`;
    const storageRef = ref(storage, filePath);

    try {
      await deleteObject(storageRef);
      const newFileLinks = fileLinks.filter((file) => file.url !== fileUrl);
      setFileLinks(newFileLinks);
      const noteRef = doc(db, "notes", noteId);

      await updateDoc(noteRef, {
        fileUrls: newFileLinks.map((file) => ({
          url: file.url,
          name: file.name,
        })),
      });

      showCustomToast("File successfully deleted!", "success");
    } catch (error) {
      console.log("Error deleting file:", error);
      showCustomToast("Error deleting file", "error");
    }
  };

  const uploadFilesAndGetUrls = async (noteId, filesToUpload) => {
    const storage = getStorage();
    const uploadedFileUrls = [];

    for (const file of filesToUpload) {
      const filePath = `Documents/medicalNotes/${noteId}/${file.name}`;
      const storageRef = ref(storage, filePath);

      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);
      uploadedFileUrls.push({ url: fileUrl, name: file.name });
    }

    return uploadedFileUrls;
  };

  const handleUpdateMedicalNote = async (
    note,
    noteId,
    effectiveDateTime,
    newFiles
  ) => {
    if (quickNoteText && effectiveDateTime) {
      setIsLoading(true);
      try {
        const noteRef = doc(db, "notes", noteId);
        const newFileUrls = await uploadFilesAndGetUrls(noteId, newFiles);
        const updatedFileLinks = [...fileLinks, ...newFileUrls];

        const noteData = {
          noteContent: note,
          updateAt: serverTimestamp(),
          doneBy: currentUser.displayName,
          effectiveDateTime: effectiveDateTime,
          fileUrls: updatedFileLinks,
        };

        await updateDoc(noteRef, noteData);

        showCustomToast("Note successfully updated!", "success");
        setIsLoading(false);
        props.closemodal();
      } catch (error) {
        console.log("error updating note :", error);
        showCustomToast("Problem when updating note", "error");
        setIsLoading(false);
      }
    } else {
      showCustomToast("Some mandatory fields are empty. Please check.", "error");
    }
  };

  const handleChange = (event) => {
    setQuickNoteText(event.target.value);
    setHasChanged(true);
  };

  return (
    <div className="edit-medical-note-modal">
      <div className="edit-medical-note-header">
        <div>
          <h2>Edit medical note</h2>
          <p>Update the medical note, effective date, and attachments.</p>
        </div>
        <button
          type="button"
          className="edit-medical-note-close"
          onClick={props.closemodal}
          aria-label="Close edit medical note modal"
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>

      <div className="edit-medical-note-body">
        <section className="edit-medical-note-section">
          <div className="edit-medical-note-section-title">
            <span>Note details</span>
          </div>

          <div className="edit-medical-note-textarea">
            <div className="edit-medical-note-label">
              Note content <span>*</span>
            </div>
            <TextField
              name="medicalNote"
              value={quickNoteText}
              onChange={handleChange}
              placeholder="Type medical note here"
              multiline
              rows={10}
              fullWidth
            />
          </div>

          <div className="edit-medical-note-date-row">
            <DatePickerWithTimeComponent
              title="Effective date and time"
              value={effectiveDateTime}
              onChange={handleDateTimeChange}
            />
          </div>
        </section>

        <section className="edit-medical-note-section">
          <div className="edit-medical-note-section-title">
            <span>Attachments</span>
            <strong>{fileLinks.length + files.length} files</strong>
          </div>

          <div className="edit-medical-note-uploader">
            <UploaderDocForNote
              handleFileChange={handleFileChange}
              files={files}
            />
            <p>Max 3 files per update.</p>
          </div>

          <div className="edit-medical-note-files">
            <UploadedFilesComponent
              fileLinks={fileLinks}
              deleteFile={deleteFile}
              noteId={props.noteId}
            />
          </div>
        </section>
      </div>

      <div className="edit-medical-note-footer">
        <Button
          variant="outlined"
          className="edit-medical-note-cancel"
          onClick={props.closemodal}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          className="edit-medical-note-save"
          disabled={!hasChanged || isLoading}
          onClick={() =>
            handleUpdateMedicalNote(
              quickNoteText,
              props.noteId,
              effectiveDateTime,
              files
            )
          }
        >
          {isLoading ? "Updating..." : "Update note"}
        </Button>
      </div>
    </div>
  );
}

export default EditMedicalNote;
